/**
 * VendorPortal.js — Transient scoped-access portal for dispatched trades.
 * Route: /vendor-portal/:workOrderId
 *
 * work_order_id acts as bearer token. Vendor sees ONLY:
 *   - the scoped work order (no owner PII, no full address)
 *   - fair-labor baseline
 *   - bid ledger for THIS order
 *   - bid submission form
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import TradeEnrichmentPanel from "./components/TradeEnrichmentPanel";
import { LienWaivers } from "./components/vendor/LienWaivers";
import { VendorJobTools } from "./components/vendor/VendorJobTools";

const BACKEND_URL = "";
const NAVY = "#020A1F"; const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";

const shell = { minHeight: "100dvh", background: NAVY, color: INK, padding: "clamp(16px,3vw,32px)", fontFamily: "'Inter', system-ui, sans-serif", boxSizing: "border-box" };
const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(14px,2.5vw,22px)", marginBottom: 16 };
const btn = { background: CYAN, color: NAVY, border: 0, padding: "12px 22px", fontWeight: 700, borderRadius: 8, cursor: "pointer", fontSize: 13, letterSpacing: "0.03em" };
const input = { width: "100%", background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, color: INK, padding: "10px 12px", borderRadius: 6, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" };

export default function VendorPortal() {
  const { workOrderId } = useParams();
  const [wo, setWo] = useState(null);
  const [bids, setBids] = useState([]);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ vendor_name: "", vendor_email: "", labor_cost: "", materials_cost: "", eta_hours: "24", notes: "" });
  const [submitted, setSubmitted] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/vendor-portal/${workOrderId}`);
      if (!r.ok) { setError("Work order not found or already closed."); return; }
      setWo(await r.json());
      const b = await fetch(`${BACKEND_URL}/api/vendor-portal/${workOrderId}/bids`).then(x => x.json());
      setBids(b.bids || []);
    } catch (e) { setError("Portal unreachable."); }
  }, [workOrderId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSubmitted(null); setError(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/vendor-portal/${workOrderId}/bid`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          labor_cost: parseFloat(form.labor_cost || 0),
          materials_cost: parseFloat(form.materials_cost || 0),
          eta_hours: parseInt(form.eta_hours || 0),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Bid rejected");
      setSubmitted(d.bid);
      load();
    } catch (e) { setError(e.message); }
  };

  if (error && !wo) return (
    <div style={shell} data-testid="vendor-portal-shell">
      <div style={{ ...card, borderColor: "#f04040", background: "rgba(240,64,64,0.1)" }} data-testid="vendor-portal-error">{error}</div>
    </div>
  );
  if (!wo) return <div style={shell} data-testid="vendor-portal-shell">Loading scoped work order…</div>;

  return (
    <div style={shell} data-testid="vendor-portal-shell">
      <div style={{ ...card, borderColor: `${CYAN}44` }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
          MOOVIN FORWARD LLC · TRANSIENT VENDOR PORTAL · SCOPED ACCESS
        </div>
        <h1 style={{ margin: 0, fontSize: "clamp(20px, 3vw, 26px)" }} data-testid="vendor-portal-title">{wo.trade_label}</h1>
        <div style={{ color: CHROME, marginTop: 6, fontSize: 13 }}>
          Work Order <code style={{ color: CYAN }}>{wo.work_order_id}</code> · SLA {wo.sla_hours}h
        </div>
      </div>

      <div style={card} data-testid="vendor-portal-scope">
        <h3 style={{ marginTop: 0, color: CYAN, fontSize: 12, letterSpacing: "0.15em" }}>SCOPE (this order only)</h3>        <div style={{ display: "grid", gap: 8, fontSize: 14 }}>
          <div><strong>Zone:</strong> {wo.zone || "n/a"}</div>
          <div><strong>Code Reference:</strong> {wo.code_ref}</div>
          <div><strong>Severity:</strong> {wo.severity}</div>
          <div><strong>Notes:</strong> {wo.scope_notes || "-"}</div>
          <div><strong>Address:</strong> <code style={{ color: CYAN, fontSize: 11 }}>hash:{wo.address_hash}</code> <span style={{ color: CHROME, fontSize: 11 }}>(full address disclosed after bid acceptance)</span></div>
          <div><strong>Fair Labor Baseline:</strong> ${wo.fair_labor_baseline_usd.toLocaleString()} · zero_markup_padding enforced</div>
        </div>
      </div>

      <LienWaivers workOrderId={workOrderId} />
      <VendorJobTools workOrderId={workOrderId} />
      <TradeEnrichmentPanel workOrderId={workOrderId} />

      <div style={card}>
        <h3 style={{ marginTop: 0, color: CYAN, fontSize: 12, letterSpacing: "0.15em" }}>SUBMIT YOUR BID</h3>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          {[
            ["vendor_name", "Vendor Legal Name", "text"],
            ["vendor_email", "Vendor Email", "email"],
            ["labor_cost", "Labor Cost (USD)", "number"],
            ["materials_cost", "Materials Cost (USD)", "number"],
            ["eta_hours", "ETA (hours)", "number"],
          ].map(([k, label, type]) => (
            <label key={k}>
              <div style={{ fontSize: 12, color: CHROME, marginBottom: 4 }}>{label}</div>
              <input type={type} style={input} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
                data-testid={`vendor-bid-${k}`} />
            </label>
          ))}
        </div>
        <label style={{ display: "block", marginTop: 10 }}>
          <div style={{ fontSize: 12, color: CHROME, marginBottom: 4 }}>Notes</div>
          <textarea style={{ ...input, minHeight: 60 }} value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            data-testid="vendor-bid-notes" />
        </label>
        <div style={{ marginTop: 12 }}>
          <button style={btn} onClick={submit} data-testid="vendor-bid-submit">Submit Bid</button>
        </div>
        {error && <div style={{ marginTop: 10, color: "#f04040" }} data-testid="vendor-portal-bid-error">{error}</div>}
        {submitted && (
          <div style={{ marginTop: 10, color: submitted.flagged_for_ceo_review ? "#f5a942" : CYAN }} data-testid="vendor-bid-success">
            ✓ Bid <code>{submitted.bid_id}</code> submitted. Total ${submitted.total_bid.toLocaleString()}.
            {submitted.flagged_for_ceo_review && <div>⚑ Flagged for CEO review: {submitted.flag_reason}</div>}
          </div>
        )}
      </div>

      {bids.length > 0 && (
        <div style={card} data-testid="vendor-portal-bid-ledger">
          <h3 style={{ marginTop: 0, color: CYAN, fontSize: 12, letterSpacing: "0.15em" }}>BID LEDGER · THIS ORDER</h3>
          {bids.map(b => (
            <div key={b.bid_id} style={{ padding: "8px 0", borderTop: `1px solid ${CHROME}22`, fontSize: 13 }}>
              <div><strong>{b.vendor_name}</strong> · ${b.total_bid.toLocaleString()} · ETA {b.eta_hours}h
                {b.flagged_for_ceo_review && <span style={{ color: "#f5a942", marginLeft: 8 }}>⚑ flagged</span>}
              </div>
              {b.notes && <div style={{ color: CHROME, fontSize: 12 }}>{b.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
